import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Users, UserPlus, ShieldCheck, Loader2, X, Pencil, Power, Trash2, FolderKanban, Search } from "lucide-react";
import {
  getTeamMembers,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
  getBvUsers,
  createBvUser,
  updateBvUser,
  deleteBvUser,
} from "../../services/team";
import { fetchRoles, fetchRoleAssignments, createRoleAssignment, deleteRoleAssignment } from "../../services/rbac";
import { fetchProjects } from "../../services/projects";
import PageHeader from "../../components/PageHeader";
import Tabs from "../../components/Tabs";
import ConfirmationModal from "../../components/ConfirmationModal";

const EMPTY = { name: "", email: "", mobileNoWa: "" };
const PAGE = 25;
const asList = (r) => (Array.isArray(r) ? r : r?.items || []);
const pId = (p) => p.projectId ?? p.id;
const pName = (p) => p.projectName ?? p.name ?? `Project ${pId(p)}`;
const pDev = (p) => p.developer ?? p.developerName ?? "";

const TABS = [
  { key: "admins", label: "Admins" },
  { key: "bv", label: "BV Users" },
];

/**
 * BuildVizer internal team — one stop for managing internal staff. "Admins" are full internal
 * admins (root protected); "BV Users" are field associates assigned to developer projects. Handles
 * create / edit / activate / delete for both, plus per-project assignment for BV Users at scale.
 */
const Team = () => {
  const [tab, setTab] = useState("admins");
  const [members, setMembers] = useState([]);
  const [bvUsers, setBvUsers] = useState([]);
  const [bvRoleId, setBvRoleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // create/edit modal
  const [modal, setModal] = useState(null); // { editing } | { editing: member } | null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // member pending deletion
  const [deleting, setDeleting] = useState(false);

  // project-assignment drawer (BV Users)
  const [projUser, setProjUser] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [nameCache, setNameCache] = useState({});
  const [projSearch, setProjSearch] = useState("");
  const [projResults, setProjResults] = useState([]);
  const [projPage, setProjPage] = useState(1);
  const [projHasMore, setProjHasMore] = useState(false);
  const [projLoading, setProjLoading] = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, bv, roles] = await Promise.all([getTeamMembers(), getBvUsers(), fetchRoles()]);
      setMembers(asList(m));
      setBvUsers(asList(bv));
      const bvRole = asList(roles).find((r) => r.code === "BV_ASSOCIATE");
      setBvRoleId(bvRole ? bvRole.rbacRoleId : null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isBv = tab === "bv";
  const rows = isBv ? bvUsers : members;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.name || ""} ${r.email || ""} ${r.username || ""}`.toLowerCase().includes(q));
  }, [rows, search]);

  /* ---------- create / edit ---------- */
  const openCreate = () => {
    setForm(EMPTY);
    setModal({ editing: null });
  };
  const openEdit = (m) => {
    setForm({ name: m.name || "", email: m.email || "", mobileNoWa: m.mobileNoWa || "" });
    setModal({ editing: m });
  };
  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setSaving(true);
    try {
      const editing = modal.editing;
      if (editing) {
        await (isBv ? updateBvUser : updateTeamMember)(editing.loginId, form);
        toast.success("Updated");
      } else {
        await (isBv ? createBvUser : createTeamMember)(form);
        toast.success("Added — login credentials have been emailed");
      }
      setModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await (isBv ? updateBvUser : updateTeamMember)(m.loginId, { active: !m.active });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const doRemove = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await (isBv ? deleteBvUser : deleteTeamMember)(confirmDelete.loginId);
      toast.success("Deleted");
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------- BV project assignment ---------- */
  const cacheProjects = (list) =>
    setNameCache((prev) => {
      const next = { ...prev };
      list.forEach((p) => (next[pId(p)] = { name: pName(p), developer: pDev(p) }));
      return next;
    });

  const searchProjects = useCallback(async (term, page) => {
    setProjLoading(true);
    try {
      const params = { page, pageSize: PAGE };
      if (term.trim()) {
        params.search = term.trim();
        params.searchField = "project_name";
      }
      const list = asList(await fetchProjects(params));
      cacheProjects(list);
      setProjResults((prev) => (page === 1 ? list : [...prev, ...list]));
      setProjHasMore(list.length === PAGE);
      setProjPage(page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load projects");
    } finally {
      setProjLoading(false);
    }
  }, []);

  const openProjects = async (u) => {
    setProjUser(u);
    setProjSearch("");
    setProjResults([]);
    setProjPage(1);
    try {
      const a = asList(await fetchRoleAssignments({ loginId: u.loginId }));
      setAssignments(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load assignments");
    }
    searchProjects("", 1);
  };

  // debounce project search
  useEffect(() => {
    if (!projUser) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchProjects(projSearch, 1), 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [projSearch, projUser, searchProjects]);

  const assignmentByProject = useMemo(() => {
    const m = {};
    assignments.forEach((a) => (m[a.projectId] = a));
    return m;
  }, [assignments]);

  const toggleProject = async (project) => {
    const projectId = pId(project);
    const existing = assignmentByProject[projectId];
    try {
      if (existing) {
        await deleteRoleAssignment(existing.assignmentId);
      } else {
        if (!bvRoleId) return toast.error("BV Associate role not found");
        await createRoleAssignment({ loginId: projUser.loginId, rbacRoleId: bvRoleId, projectId });
      }
      setAssignments(asList(await fetchRoleAssignments({ loginId: projUser.loginId })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update assignment");
    }
  };

  const projLabel = (id) => nameCache[id]?.name || `Project ${id}`;

  const tabsWithCounts = TABS.map((t) => ({ ...t, count: t.key === "bv" ? bvUsers.length : members.length }));

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Users}
        title="Team"
        subtitle="Manage BuildVizer internal staff — admins and field associates"
        actions={
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-500 px-3 py-2 text-sm font-medium text-white hover:bg-flamingo-600 transition-colors"
          >
            <UserPlus size={15} /> {isBv ? "Add BV User" : "Add Admin"}
          </button>
        }
      />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <Tabs tabs={tabsWithCounts} active={tab} onChange={(k) => { setTab(k); setSearch(""); }} />

        <div className="p-4 lg:p-5 flex flex-col gap-3">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${isBv ? "BV Users" : "admins"}…`}
              className="w-full sm:w-64 rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200"
            />
          </div>

          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" /> Loading team…
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((m) => (
                <div key={m.loginId} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold text-slate-800">{m.name || m.username}</p>
                  {m.isRoot && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                      <ShieldCheck size={11} /> Root
                    </span>
                  )}
                  {!m.active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">Inactive</span>}
                </div>
                <p className="truncate text-xs text-slate-500">
                  {m.email} · {m.roleLabel}
                  {isBv ? ` · ${m.projectCount || 0} project${m.projectCount === 1 ? "" : "s"}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {isBv && (
                  <button onClick={() => openProjects(m)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Manage projects">
                    <FolderKanban size={15} />
                  </button>
                )}
                <button onClick={() => openEdit(m)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Edit">
                  <Pencil size={15} />
                </button>
                {!m.isRoot && (
                  <>
                    <button
                      onClick={() => toggleActive(m)}
                      className={`rounded-lg p-2 hover:bg-slate-100 ${m.active ? "text-amber-500" : "text-emerald-600"}`}
                      title={m.active ? "Deactivate" : "Reactivate"}
                    >
                      <Power size={15} />
                    </button>
                    <button onClick={() => setConfirmDelete(m)} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50" title="Delete">
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
              ))}
              {filtered.length === 0 && <p className="text-sm text-slate-400">{search ? "No matches." : `No ${isBv ? "BV Users" : "admins"} yet.`}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Create / edit modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">
                {modal.editing ? "Edit" : isBv ? "Add BV User" : "Add Admin"}
              </h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="space-y-3 px-5 py-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-flamingo-500 focus:outline-none" />
              <div>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!modal.editing} placeholder="Email (login)" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-flamingo-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400" />
                {!modal.editing && <p className="mt-1 text-[11px] text-slate-400">Login credentials are generated and emailed to this address.</p>}
              </div>
              <input value={form.mobileNoWa} onChange={(e) => setForm({ ...form, mobileNoWa: e.target.value })} placeholder="Mobile (optional)" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-flamingo-500 focus:outline-none" />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button onClick={() => setModal(null)} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={submit} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-flamingo-600 disabled:opacity-50">
                {saving ? <Loader2 size={15} className="animate-spin" /> : null} {modal.editing ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BV project-assignment drawer */}
      {projUser && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" role="dialog" aria-modal="true">
          <div className="flex h-full w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Project assignments</h3>
                <p className="text-xs text-slate-500">{projUser.name || projUser.username}</p>
              </div>
              <button onClick={() => setProjUser(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>

            {/* Assigned chips */}
            <div className="border-b border-slate-100 px-5 py-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Assigned ({assignments.length})</p>
              {assignments.length === 0 ? (
                <p className="text-xs text-slate-400">Not assigned to any project yet.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {assignments.map((a) => (
                    <span key={a.assignmentId} className="inline-flex items-center gap-1 rounded-full bg-flamingo-50 px-2 py-1 text-xs text-flamingo-700">
                      {projLabel(a.projectId)}
                      <button onClick={() => toggleProject({ projectId: a.projectId })} className="text-flamingo-400 hover:text-flamingo-700" title="Remove"><X size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Searchable project picker */}
            <div className="border-b border-slate-100 px-5 py-3">
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={projSearch} onChange={(e) => setProjSearch(e.target.value)} placeholder="Search projects to assign…" className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-2">
              {projResults.map((p) => {
                const assigned = !!assignmentByProject[pId(p)];
                return (
                  <label key={pId(p)} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50">
                    <input type="checkbox" checked={assigned} onChange={() => toggleProject(p)} className="h-4 w-4 rounded border-slate-300 text-flamingo-600" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-slate-800">{pName(p)}</span>
                      {pDev(p) ? <span className="block truncate text-xs text-slate-400">{pDev(p)}</span> : null}
                    </span>
                  </label>
                );
              })}
              {projLoading && <div className="flex items-center gap-2 p-3 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
              {!projLoading && projResults.length === 0 && <p className="p-3 text-sm text-slate-400">No projects found.</p>}
              {projHasMore && !projLoading && (
                <button onClick={() => searchProjects(projSearch, projPage + 1)} className="mt-1 w-full rounded-lg border border-slate-200 py-2 text-sm text-slate-600 hover:bg-slate-50">
                  Load more
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!confirmDelete}
        title="Delete team member"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        isLoading={deleting}
        loadingText="Deleting…"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doRemove}
      >
        Permanently delete <span className="font-semibold">{confirmDelete?.name || confirmDelete?.email}</span>? This cannot be undone.
      </ConfirmationModal>
    </div>
  );
};

export default Team;
